import React, { useCallback, useEffect, useState } from 'react';
import { Undo2, CalendarClock, Loader2, XCircle, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';

const fmt = (iso) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? "à l'instant" : m < 60 ? `il y a ${m} min` : m < 1440 ? `il y a ${Math.round(m / 60)} h` : `il y a ${Math.round(m / 1440)} j`;
};
const STATUS = { scheduled: ['Programmée', 'bg-blue-50 text-blue-700'], active: ['En cours', 'bg-green-50 text-green-700'] };

export const PricingToolsPanel = ({ refreshKey, onChanged }) => {
  const [lastOp, setLastOp] = useState(null);
  const [promos, setPromos] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [ops, pr] = await Promise.all([adminAPI.bulkPriceOps(1), adminAPI.promos()]);
      setLastOp(ops.items[0] || null);
      setPromos(pr.items.filter(p => ['scheduled', 'active'].includes(p.status)));
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const opLabel = (op) => (op.kind === 'stock'
    ? `stock ${op.mode === 'set' ? `= ${op.value}` : `${op.value > 0 ? '+' : ''}${op.value}`}`
    : `${op.percent > 0 ? '+' : ''}${op.percent} %`);
  const undo = async () => {
    if (!window.confirm(`Restaurer ${lastOp.kind === 'stock' ? 'les stocks' : 'les prix'} d'avant l'opération « ${lastOp.label || 'prix groupé'} ${opLabel(lastOp)} » sur ${lastOp.product_count} produit(s) ?`)) return;
    setBusy('undo');
    try {
      const r = await adminAPI.undoBulkPriceOp(lastOp.id);
      toast.success(`${r.message}${r.skipped?.length ? ` · ${r.skipped.length} ignoré(s) (${[...new Set(r.skipped.map(s => s.reason))].join(', ')})` : ''}`);
      onChanged?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Annulation impossible'); }
    finally { setBusy(''); }
  };
  const cancel = async (p) => {
    if (!window.confirm(p.status === 'active' ? `Arrêter la promo « ${p.label} » maintenant et restaurer les prix ?` : `Annuler la promo programmée « ${p.label} » ?`)) return;
    setBusy(p.id);
    try { const r = await adminAPI.cancelPromo(p.id); toast.success(r.message); onChanged?.(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); }
    finally { setBusy(''); }
  };

  const undoable = lastOp && !lastOp.undone_at;
  if (!undoable && promos.length === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="pricing-tools-panel">
      {undoable && (
        <button type="button" onClick={undo} disabled={!!busy} data-testid="bulk-price-undo" className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-60">
          {busy === 'undo' ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}
          Annuler la dernière opération {lastOp.kind === 'stock' ? 'de stock' : 'de prix'} — {opLabel(lastOp)} sur {lastOp.product_count} produit(s){lastOp.label && lastOp.kind !== 'stock' ? ` (${lastOp.label})` : ''}, {ago(lastOp.created_at)}
        </button>
      )}
      {promos.map(p => (
        <span key={p.id} data-testid={`promo-chip-${p.id}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700">
          <Tag size={13} className="text-orange-500" />
          <strong>{p.label}</strong> {p.percent} % · {p.product_count} produit(s)
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS[p.status][1]}`}>{STATUS[p.status][0]}</span>
          <span className="text-gray-500 inline-flex items-center gap-1"><CalendarClock size={12} /> {p.status === 'active' ? `jusqu'au ${fmt(p.end_at)}` : `du ${fmt(p.start_at)} au ${fmt(p.end_at)}`}</span>
          <button type="button" onClick={() => cancel(p)} disabled={!!busy} data-testid={`promo-cancel-${p.id}`} title={p.status === 'active' ? 'Arrêter maintenant' : 'Annuler'} className="text-gray-400 hover:text-red-600">
            {busy === p.id ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={14} />}
          </button>
        </span>
      ))}
    </div>
  );
};
